import React, { useEffect, useRef, useState } from "react";
import client from "../api/client";
import { useAuth } from "../context/AuthContext";
import Stepper from "./Stepper";
import TermsModal from "./TermsModal";

const MIN_AMOUNT = 10; // EUR

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function CryptoCalculator() {
  const { refreshUser } = useAuth();

  // --- asset / amount selection --------------------------------------------
  const [assets, setAssets] = useState([]); // [{symbol, name, blockchains}]
  const [symbol, setSymbol] = useState("");
  const [chain, setChain] = useState("");
  const [amount, setAmount] = useState("");
  const debouncedAmount = useDebouncedValue(amount, 500);

  // --- live quote (the ticker) ---------------------------------------------
  const [quoteTokens, setQuoteTokens] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [ticking, setTicking] = useState(false);

  // --- flow state -----------------------------------------------------------
  const [showTerms, setShowTerms] = useState(false);
  const [stepKey, setStepKey] = useState("quote"); // quote | kyc | payment | done
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [orderId, setOrderId] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  const [paymentState, setPaymentState] = useState(null);
  const [txid, setTxid] = useState(null);

  // URLs we redirect the user to (also shown as manual fallback links)
  const [kycUrl, setKycUrl] = useState(null);
  const [paymentUrl, setPaymentUrl] = useState(null);

  // Have we ever seen a non-"fail" kyc state this attempt? Guards against
  // flashing "rejected" when the provider hands back a stale/initial "fail".
  const kycSawProgress = useRef(false);

  const pollRef = useRef(null);

  // --- load coins/chains live from CryptoPocket ----------------------------
  useEffect(() => {
    client
      .get("/api/cryptopocket/assets")
      .then(({ data }) => {
        const list = data?.data || [];
        setAssets(list);
        if (list.length) {
          setSymbol(list[0].symbol);
          setChain(list[0].blockchains?.[0] || "");
        }
      })
      .catch(() => setError("No se pudieron cargar las criptomonedas disponibles."));
  }, []);

  // keep the chain valid whenever the symbol changes
  useEffect(() => {
    const asset = assets.find((a) => a.symbol === symbol);
    if (asset && chain && !asset.blockchains.includes(chain)) {
      setChain(asset.blockchains?.[0] || "");
    }
  }, [symbol, assets]);

  // --- live quote -----------------------------------------------------------
  useEffect(() => {
    const amt = Number(debouncedAmount);
    if (!amt || amt < MIN_AMOUNT || !symbol || !chain) {
      setQuoteTokens(null);
      return;
    }
    let cancelled = false;
    setQuoteLoading(true);
    client
      .post("/api/cryptopocket/quote", { amount: amt, symbol, chain, paymentType: "card" })
      .then(({ data }) => {
        if (cancelled) return;
        setQuoteTokens(data?.data?.tokens ?? null);
        setTicking(true);
        setTimeout(() => setTicking(false), 450);
      })
      .catch(() => !cancelled && setQuoteTokens(null))
      .finally(() => !cancelled && setQuoteLoading(false));
    return () => {
      cancelled = true;
    };
  }, [debouncedAmount, symbol, chain]);

  useEffect(() => () => clearInterval(pollRef.current), []);

  // Point a (possibly pre-opened) tab at a url, falling back to a fresh window.
  const sendTabTo = (pendingWin, url) => {
    if (!url) return false;
    try {
      if (pendingWin && !pendingWin.closed) {
        pendingWin.location.href = url;
        return true;
      }
    } catch (_) {
      /* blocked — fall through to a fresh window */
    }
    window.open(url, "_blank", "noopener,noreferrer");
    return true;
  };

  // --- polling helpers --------------------------------------------------------
  const pollKyc = (orderIdToContinue) => {
    clearInterval(pollRef.current);
    kycSawProgress.current = false;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await client.get("/api/cryptopocket/kyc-status");
        const st = data.kyc_status;

        if (st === "valid") {
          clearInterval(pollRef.current);
          await refreshUser();
          const { data: cont } = await client.post(
            `/api/cryptopocket/orders/${orderIdToContinue}/continue`
          );
          if (cont.status === "ok") {
            setPaymentId(cont.payment_id);
            setPaymentUrl(cont.gateway_url || null);
            setStepKey("payment");
            if (cont.gateway_url) window.open(cont.gateway_url, "_blank", "noopener,noreferrer");
            pollPayment(cont.payment_id);
          } else {
            setError("No se pudo iniciar el pago tras la verificación.");
          }
          return;
        }

        // A real rejection carries a reason. A "fail" with no reason before the
        // user has made any progress is usually a stale/initial state, not a
        // decline — keep waiting instead of scaring them off.
        if (st === "fail" && (data.kyc_rejected_reason || kycSawProgress.current)) {
          clearInterval(pollRef.current);
          setError("La verificación de identidad fue rechazada.");
          return;
        }

        if (st && st !== "fail" && st !== "unused") {
          kycSawProgress.current = true; // e.g. "processing"
        }
        // "unused" / "processing" / benign "fail" -> keep polling
      } catch (err) {
        // transient network errors: keep trying on the next tick
      }
    }, 5000);
  };

  const pollPayment = (pid) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await client.get(`/api/cryptopocket/payment/${pid}`);
        if (data.status === "ok") {
          setPaymentState(data.data.status);
          if (data.data.status === "coins_sent") {
            clearInterval(pollRef.current);
            setTxid(data.data.txid);
            setStepKey("done");
          }
        }
      } catch (err) {
        // transient network errors: keep trying on the next tick
      }
    }, 6000);
  };

  // --- submit flow -----------------------------------------------------------
  const startOrder = () => {
    const amt = Number(amount);
    if (!symbol || !chain || !amt || amt < MIN_AMOUNT) {
      setError(`Ingresa un monto válido (mínimo ${MIN_AMOUNT} EUR).`);
      return;
    }
    setError("");
    setShowTerms(true);
  };

  const confirmOrder = async () => {
    setBusy(true);
    setError("");

    // Open the tab synchronously (inside the click) so popup blockers don't
    // kill it after the await. We point it at the real URL once we have it.
    const pending = window.open("", "_blank");

    try {
      const { data } = await client.post("/api/cryptopocket/orders/guided", {
        coinSymbol: symbol,
        coinChain: chain,
        fiatAmount: Number(amount),
      });

      console.log("guided order response:", data);

      setShowTerms(false);

      if (data.status === "kyc_required") {
        const url = data.kyc_form_url;
        if (!url) {
          if (pending && !pending.closed) pending.close();
          setError("El proveedor no devolvió el enlace de verificación (kyc_form_url vacío).");
          return;
        }
        setOrderId(data.order_id);
        setKycUrl(url);
        setStepKey("kyc");
        sendTabTo(pending, url); // <-- the actual Didit redirect
        pollKyc(data.order_id);
      } else if (data.status === "ok") {
        const url = data.gateway_url;
        setOrderId(data.order_id);
        setPaymentId(data.payment_id);
        setPaymentUrl(url || null);
        setStepKey("payment");
        if (url) sendTabTo(pending, url);
        else if (pending && !pending.closed) pending.close();
        pollPayment(data.payment_id);
      } else {
        if (pending && !pending.closed) pending.close();
        setError(data.message || "No se pudo crear la orden.");
      }
    } catch (err) {
      if (pending && !pending.closed) pending.close();
      setError("Error de conexión. Intenta de nuevo.");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    clearInterval(pollRef.current);
    setStepKey("quote");
    setAmount("");
    setQuoteTokens(null);
    setOrderId(null);
    setPaymentId(null);
    setPaymentState(null);
    setTxid(null);
    setKycUrl(null);
    setPaymentUrl(null);
    setError("");
  };

  const selectedAsset = assets.find((a) => a.symbol === symbol);

  return (
    <div className="calc-card">
      <Stepper activeKey={stepKey} />

      {(stepKey === "quote") && (
        <div className="calc-body">
          <div className="field-row">
            <div className="field">
              <label>Moneda</label>
              <select value={symbol} onChange={(e) => setSymbol(e.target.value)}>
                {assets.map((a) => (
                  <option key={a.symbol} value={a.symbol}>
                    {a.name} · {a.symbol}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Red</label>
              <select value={chain} onChange={(e) => setChain(e.target.value)}>
                {(selectedAsset?.blockchains || []).map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="field">
            <label>Monto a pagar</label>
            <div className="amount-input">
              <input
                inputMode="decimal"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^0-9.,]/g, ""))}
              />
              <span className="amount-suffix">EUR</span>
            </div>
            <small className="hint">Mínimo {MIN_AMOUNT} EUR</small>
          </div>

          <div className={`ticker ${ticking ? "is-ticking" : ""}`}>
            <span className="ticker-label">Recibes aprox.</span>
            <span className="ticker-value">
              {quoteLoading ? "···" : quoteTokens != null ? quoteTokens.toFixed(6) : "—"}
            </span>
            <span className="ticker-symbol">{symbol}</span>
          </div>

          {error && <p className="error-text">{error}</p>}

          <button className="btn btn-primary btn-block" onClick={startOrder}>
            Continuar
          </button>
          <p className="fine-print">
            El método de pago, billetera de destino y comisión final se confirman en el siguiente paso, dentro de
            la página segura de nuestro proveedor de pagos.
          </p>
        </div>
      )}

      {stepKey === "kyc" && (
        <div className="calc-body status-block">
          <p className="status-title">Verificación de identidad</p>
          <p className="status-text">
            Abrimos una pestaña nueva para completar tu verificación. En cuanto sea aprobada, continuaremos
            automáticamente — no cierres esta ventana.
          </p>
          {kycUrl && (
            <a className="btn btn-primary" href={kycUrl} target="_blank" rel="noopener noreferrer">
              Abrir verificación
            </a>
          )}
          {error && <p className="error-text">{error}</p>}
          <span className="spinner" />
        </div>
      )}

      {stepKey === "payment" && (
        <div className="calc-body status-block">
          <p className="status-title">Completa tu pago</p>
          <p className="status-text">
            Elige tarjeta, transferencia o PSD2 en la pestaña que abrimos, y confirma tu billetera de destino ahí.
          </p>
          {paymentUrl && (
            <a className="btn btn-primary" href={paymentUrl} target="_blank" rel="noopener noreferrer">
              Abrir pago
            </a>
          )}
          <span className="status-tag">{paymentState || "esperando pago"}</span>
        </div>
      )}

      {stepKey === "done" && (
        <div className="calc-body status-block">
          <p className="status-title status-success">Operación completada</p>
          {txid && <p className="status-mono">TX {txid}</p>}
          <button className="btn btn-ghost" onClick={reset}>
            Hacer otra compra
          </button>
        </div>
      )}

      <TermsModal open={showTerms} onConfirm={confirmOrder} onCancel={() => setShowTerms(false)} busy={busy} />
    </div>
  );
}